import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import settings from '../../config/settings';
import ClientAnalytics from '../analytics/ClientAnalytics';
import {CardClickTypes} from '../analytics/AnalyticsConst';
import * as BrowserUtils from '../utils/BrowserUtils';
import EmbedVideo from './EmbedVideo';
import * as CardUtils from '../utils/CardUtils';
import {cardLoadMode, cardStyles} from '../const/VuCardConst';
import Cookies from 'js-cookie';
import * as RedirectConst from '../const/RedirectConst';
import FrameMessages from '../const/FrameMessages';
import * as CardDestinationUtils from '../utils/CardDestinationUtils';
import {injectHtml} from '../utils/DomUtils';
import {getImageUrl} from '../utils/ApiUtils';
import DestinationTypes from '../const/DestinationTypes';

export default class VuCardSurvey extends React.Component {
  constructor(props) {
    super(props);

    this.providerTrackingCodeContainer = null;
    this.state = {
      isMobile: false,
      device: '',
      destinations: [],
      isSurveyAnswered: false
    };

    this.callToActionContainer = null;
    this.getMediaConfig = this.getMediaConfig.bind(this);
  }

  componentDidMount() {
    const {record} = this.props;
    const device = BrowserUtils.getDeviceType();
    const destinations = this.getDestinationConfigs(record, device);

    this.setState({
      isMobile: BrowserUtils.isMobile.any(),
      device,
      destinations
    });

    this.sendFrameRedirectCookieToParentWindow();
    this.setupDestinationAutoRedirect(destinations);

    this.applyCallToAction();
  }

  isCookieRedirectActive() {
    return !_.get(this.props, 'campaign.isCookieRedirectDisabled');
  }

  applyCallToAction() {
    const {callToAction} = this.props.record;

    if (callToAction) {
      const wrapper = document.createElement('span');
      this.callToActionContainer.appendChild(wrapper);
      injectHtml(wrapper, callToAction);
    }
  }

  getDestinationConfigs(record, device) {
    return _(record.destinations)
      .filter(d => d.isActive)
      .filter(d => d.kind === DestinationTypes.pollDestination)
      .filter(d => _.isEmpty(d.devices) || d.devices.indexOf(device) > -1)
      .uniqBy(d => d.name)
      .sortBy(destinationButtonsOrder)
      .value();
  }

  sendFrameRedirectCookieToParentWindow() {
    const {record, mode, isPreloaded} = this.props;
    if (this.isCookieRedirectActive() && isPreloaded && mode === cardLoadMode.iframe) {
      const cookie = Cookies.getJSON(RedirectConst.cookieName) || null;
      this.postRedirectStateToParentWindow(record.id, cookie);
    }
  }

  setupDestinationAutoRedirect(destinations) {
    const {mode} = this.props;

    if (mode === cardLoadMode.standalone) {
      const destination = _.find(destinations, {isAutoRedirect: true});

      if (destination) {
        const timeoutPeriod = (_.get(settings, 'widget.destinationRedirectTimeoutMS') || 4000) - 500;
        this.redirectTimeout = setTimeout(() => {
          this.onDestinationRedirectTimeout(destination);
        }, timeoutPeriod);
      }
    }
  }

  onDestinationRedirectTimeout(destination) {
    const {record, campaignId} = this.props;

    this.appendCommonDestinationClickTrackingCodes();
    this.appendDestinationClickTrackingCode(destination.name);

    ClientAnalytics.sendCardRedirect(
      this.getCardId(record),
      campaignId,
      RedirectConst.redirectActions.auto,
      RedirectConst.redirectTypes.cardAuto,
      destination.url,
      BrowserUtils.getDeviceType(),
      destination.name
    );

    this.redirect(destination.url);
  }

  redirect(redirectUrl) {
    setTimeout(() => {
      this.innerRedirect(redirectUrl);
    }, 1000);
  }

  /* Wrapper for 'window.location.replace' to make mocking / testing easier */
  innerRedirect(redirectUrl) {
    window.location.href = redirectUrl;
  }

  getProgramId(record) {
    return record.program ? record.program.id : record.id;
  }

  getCardId(record) {
    return record.program ? record.id : null;
  }

  render() {
    const {record} = this.props;
    const {isMobile, isSurveyAnswered} = this.state;
    const mediaConfig = this.getMediaConfig();

    if (!record) {
      return null;
    }

    let contentStyle = null;
    if (this.getBackgroundVideo() && !isMobile) {
      contentStyle = {backgroundColor: 'transparent', overflow: 'hidden'};
    } else if (this.hasBackgroundImage()) {
      contentStyle = {
        background: `url(${getImageUrl(record.backgroundImage)}) center center fixed`,
        backgroundSize: 'cover'
      };
    } else if (_.get(record, 'backgroundColor')) {
      contentStyle = {backgroundColor: record.backgroundColor};
    }

    return (
      <div className="vuCard__content"
           style={contentStyle}>
        {
          this.renderBackgroundVideo()
        }
        <div className="vuCard__content__movie">
          {
            !record.hideTitle &&
            <div className="vuCard__content__movie__title">
              {record.title}
            </div>
          }
          {
            (mediaConfig.videoUrl || mediaConfig.videoImg) ?
              <div className="vuCard__content__movie__video">
                {this.renderPreviewVideo(mediaConfig)}
              </div>
              : null
          }
          {
            mediaConfig.posterUrl ?
              <div className="vuCard__content__movie__cover">
                <img src={mediaConfig.posterUrl} alt={`${record.title} Poster`}/>
              </div>
              : null
          }
          <div className="vuCard__content__line"/>
        </div>
        <div className="vuCard__content__select">
          {
            this.setCustomStylesForCta()
          }
          {
            !isSurveyAnswered ?
              <React.Fragment>
                {
                  this.renderCta()
                }
                {
                  this.renderDestinations(record)
                }
              </React.Fragment>
              :
              this.renderThankYouMessage()
          }
        </div>
        {
          this.renderDestinationClickTrackingCodeContainer()
        }
        {
          this.renderAbout()
        }
      </div>);
  }

  renderBackgroundVideo() {
    const videoUrl = this.getBackgroundVideo();

    if (videoUrl && !this.state.isMobile) {
      return (
        <video className="vuCard__content__backgroundVideo"
               preload="metadata"
               style={{
                 height: 'auto',
                 width: 'auto',
                 minHeight: '100%',
                 minWidth: '100%',
                 position: 'absolute',
                 top: '50%',
                 left: '50%',
                 transform: 'translate(-50%, -50%)',
                 zIndex: -100,
                 transition: '1s opacity'
               }}
               src={videoUrl}
               playsinline
               autoPlay
               muted
               loop/>
      );
    }
  }

  hasBackgroundAsset() {
    return !!this.getBackgroundVideo() || this.hasBackgroundImage();
  }

  getBackgroundVideo() {
    const {record} = this.props;
    return record.backgroundVideo;
  }

  hasBackgroundImage() {
    const {record} = this.props;
    return !!getImageUrl(record.backgroundImage);
  }

  getProviderButtonStyle() {
    return this.hasBackgroundAsset() ? {backgroundColor: 'rgba(0, 0, 0, 0.5)'} : null;
  }

  setCustomStylesForCta() {
    const {callToActionColor} = this.props.record;

    if (callToActionColor) {
      return (
        <style dangerouslySetInnerHTML={{
          __html: `
.vuCard__content__select__cta {
  color: ${callToActionColor};
}
`
        }}/>
      );
    }
  }

  renderCta() {
    return (
      <div className="vuCard__content__select__cta"
           ref={(node) => {
             this.callToActionContainer = node;
           }}
      />);
  }

  renderThankYouMessage() {
    return (
      <div className="vuCard__content__select__cta">
        Thank you!
      </div>);
  }

  getMediaConfig() {
    const {record, mode} = this.props;
    const posterUrl = CardUtils.getHorizontalPosterImageUrl(record);
    const videoUrl = CardUtils.getPosterVideoUrl(record);
    const videoImg = posterUrl;

    if (mode === cardLoadMode.preview && record.isVideoPreviewBlocked) {
      return {videoUrl, videoKind: 'upload', videoImg: _.get(settings, 'create.logoUrl', '/static/logo.png')};
    }

    return videoUrl ? {videoUrl, videoKind: record.posterVideo.kind, videoImg} : {posterUrl};
  }

  renderPreviewVideo(config) {
    const {isVisible} = this.props;

    if (!isVisible) return null;

    return (
      <EmbedVideo url={config.videoUrl}
                  kind={config.videoKind}
                  poster={config.videoImg}/>);
  }

  renderDestinations() {
    if (!this.props.isVisible) {
      return null;
    }

    const destinationButtons = _.map(this.state.destinations, (d, index) => (
      this.renderSingleDestination(d, `destination-${index}`)
    ));

    return (
      <div className="vuCard__content__select__publishers">
        {destinationButtons}
      </div>
    );
  }

  renderSingleDestination(destination, key) {
    const logoUrl = CardDestinationUtils.getLogoImageUrl(destination);
    const iconStyle = {background: `url(${logoUrl}) center center`};

    return (
      <div key={key}
           style={this.getProviderButtonStyle()}
           className={`vuCard__content__select__publisher vuCard__content__select__publisher--${destination.kind}`}>
        <a title={destination.name}
           data-name={destination.name}
           onClick={this.onDestinationClick.bind(this, destination)}>
          <div className="vuCard__content__select__publisher__icon--destination"
               style={iconStyle}/>
        </a>
      </div>);
  }

  onDestinationClick(destination) {
    const {record, campaignId, collectionId, rendererId} = this.props;

    this.cancelRedirectionTimer();

    this.appendCommonDestinationClickTrackingCodes();
    this.appendDestinationClickTrackingCode(destination.name);

    ClientAnalytics.sendCardDestinationClicked(
      destination.name,
      CardClickTypes.pollDestination,
      '',
      this.getProgramId(record),
      record.title,
      rendererId,
      this.getCardId(record),
      campaignId,
      collectionId);

    this.setSurveyAnswered();
  }

  setSurveyAnswered() {
    this.callToActionContainer = null;
    this.setState({isSurveyAnswered: true});
  }

  cancelRedirectionTimer() {
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
  }

  postRedirectStateToParentWindow(cardId, cookie) {
    window.parent.postMessage({
      title: FrameMessages.setRedirectState,
      cardId,
      cookie
    }, '*');
  }

  appendDestinationClickTrackingCode(destinationName) {
    const {record} = this.props;
    const destinationConfig = _.find(record.destinations, {name: destinationName});
    const {trackingCode} = destinationConfig;

    if (trackingCode) {
      const wrapper = document.createElement('span');
      this.providerTrackingCodeContainer.appendChild(wrapper);
      injectHtml(wrapper, trackingCode);
    }
  }

  /* Legacy tracking code logic / no distinction over destination name */
  appendCommonDestinationClickTrackingCodes() {
    const {record} = this.props;

    let codes = '';
    record.trackingCodes.forEach((codeConfig) => {
        const code = CardUtils.getTrackingCode(codeConfig, this.state.device, 'destination_click');
        if (code) {
          codes += code;
        }
      }
    );

    if (codes) {
      const wrapper = document.createElement('span');
      this.providerTrackingCodeContainer.appendChild(wrapper);
      injectHtml(wrapper, codes);
    }
  }

  renderAbout() {
    return (
      <a className="vuCard__content__about"
         href="https://vupulse.com/"
         target="_blank"
         rel="noopener noreferrer">
        About VuPulse
      </a>
    );
  }

  renderDestinationClickTrackingCodeContainer() {
    return (
      <span className="vuCard__destinationTrackingCode"
            style={{position: 'absolute', bottom: '0'}}
            ref={(node) => {
              this.providerTrackingCodeContainer = node;
            }}
      />
    );
  }
}

const destinationButtonsOrder = item => (_.isNaN(item.order) ? Number.MAX_VALUE : item.order);

VuCardSurvey.propTypes = {
  record: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(Object.values(cardLoadMode)),
  rendererId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderer: PropTypes.object,
  isPreloaded: PropTypes.bool.isRequired
};

VuCardSurvey.defaultProps = {
  shareUrl: '',
  cardStyle: cardStyles.classic,
  isPreloaded: false
};
